import { ParsedCoordinatePair } from '../models/coordinate-search.model';

const DDM_TOKEN = /^(\d{1,3})°\s*(\d{1,2}\.\d+)['′]\s*([NnSsEeWw])$/;

function splitPair(input: string): [string, string] | null {
  const commaSplit = input.split(',').map((t) => t.trim()).filter(Boolean);
  if (commaSplit.length === 2) {
    return [commaSplit[0], commaSplit[1]];
  }

  const whitespaceSplit = input.split(/\s+/).filter(Boolean);
  if (whitespaceSplit.length === 2) {
    return [whitespaceSplit[0], whitespaceSplit[1]];
  }

  return null;
}

function ddmTokenToDecimal(deg: number, min: number, hemisphere: string): number {
  const sign = hemisphere.toUpperCase() === 'S' || hemisphere.toUpperCase() === 'W' ? -1 : 1;
  const abs = Math.abs(deg) + min / 60;
  return sign * abs;
}

/** DDM: 45°30.1'N 73°34.03'W */
export function parseDdmPair(input: string): ParsedCoordinatePair | null {
  const trimmed = input.trim();
  const pair = splitPair(trimmed);
  if (!pair) {
    return null;
  }
  const [latToken, lngToken] = pair;

  const latMatch = latToken.match(DDM_TOKEN);
  const lngMatch = lngToken.match(DDM_TOKEN);
  if (!latMatch || !lngMatch) {
    return null;
  }

  if (!/[NnSs]/.test(latMatch[3]) || !/[EeWw]/.test(lngMatch[3])) {
    return null;
  }

  const latDeg = Number(latMatch[1]);
  const latMin = Number(latMatch[2]);
  const lngDeg = Number(lngMatch[1]);
  const lngMin = Number(lngMatch[2]);

  if (
    [latDeg, latMin, lngDeg, lngMin].some((n) => !Number.isFinite(n)) ||
    latMin >= 60 ||
    lngMin >= 60
  ) {
    return null;
  }

  const lat = ddmTokenToDecimal(latDeg, latMin, latMatch[3]);
  const lng = ddmTokenToDecimal(lngDeg, lngMin, lngMatch[3]);

  return {
    lat,
    lng,
    sourceFormat: 'ddm',
    rawInput: input,
  };
}
